import { Button, Typography } from "@mui/material";
import { makeStyles } from "@mui/styles";
import { useGridApiRef } from "@mui/x-data-grid-pro";
import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchLatestEmployeeTradeEvents, fetchTradeDataByReferenceNumber } from "../api/trade";
import { isUserAuthorizedByServer } from "../api/userAccess";
import EmployeeTradeTable from "../components/EmployeeTradeTable";
import PageLoading from "../components/PageLoading";
import TradeFilterForm from "../components/TradeFilterForm";
import UnauthorizedMessage from "../components/UnauthorizedMessage";
import StarsBreadcrumbs from "../components/fundamentals/StarsBreadcrumbs";
import {
  selectEmployeeTradeData,
  updateDateTypeFilter,
  updateEndDateFilter,
  updateEndDateFilterErrors,
  updateIsMobileFilterDialogOpen,
  updateMobileSortByValue,
  updateSearchString,
  updateSearchStringErrors,
  updateStartDateFilter,
  updateStartDateFilterErrors,
  updateStatusFilters,
  updateTradeFoundByReferenceNumberSearch,
  updateTradeFoundByReferenceNumberSearchIsDialogOpen,
  updateTradeFoundByReferenceNumberSearchIsLoading,
  updateTradeTypeFilters,
  updateWorkgroupFilters,
} from "../slices/employeeTrade/employeeTradeSlice";
import { selectUserSession } from "../slices/userSession/userSessionSlice";
import { selectWebSocketEventData } from "../slices/webSocketEvent/webSocketEventSlice";
import { logout } from "../util/authentication";
import {
  ALL_STATUSES,
  ALL_TRADE_TYPES,
  BREADCRUMBS_TITLE,
  ERROR_MAX_PAYLOAD_LIMIT_MSG,
  FEATURES,
  HOME_PATH,
} from "../util/constants";
import { deserializeDateFromRedux, serializeDateToRedux, set_time_to_end_of_day } from "../util/dateTimeFormatter";
import { tradeDataErrorCodeToErrorMessageMap } from "../util/errorMapper";
import {
  DEFAULT_EMPLOYEE_TRADES_END_DATE_FILTER,
  DEFAULT_EMPLOYEE_TRADES_START_DATE_FILTER,
  DEFAULT_EMPLOYEE_TRADES_STATUS_FILTERS,
  DEFAULT_EMPLOYEE_TRADES_TRADE_TYPE_FILTERS,
  filterTrades,
} from "../util/filterTrades";
import { validateEndDateFilter, validateStartDateFilter } from "../util/inputValidation";
import { REQUEST_TRADE_TITLE } from "../util/messageConstants";
import { initializeSearchIndex, searchTrades } from "../util/searchTrades";
import { sortTradesByFilter } from "../util/sortTrades";
import { getScrubbedTradeFormEvent, getScrubbedTradeFormEvents } from "../util/tradeFormEventScrubber";
import { mergeWebSocketEmpTrades } from "../util/webSocketUtils";
import { isManager } from "../util/workgroupMapper";
import { selectWorkgroupData } from "../slices/commonConfig/commonConfigSlice";
import { getTradeWorkgroups } from "../util/tradeUtils";

const useStyles = makeStyles((theme) => ({
  requestTradeButtonDesktop: {
    height: "40px",
    fontSize: "0.8rem",
    float: "right",
    marginRight: ".9vw",
    marginBottom: "10px",
  },
  requestTradeButtonMobile: {
    width: "95.45vw",
  },
  buttonText: {
    color: theme.palette.secondary.swaWhite,
  },
}));

const MyTradesPage = (props) => {
  const classes = useStyles();
  const employeeTradesPageTitle = "My\nTrades";
  const userSession = useSelector(selectUserSession);
  const employeeTradeData = useSelector(selectEmployeeTradeData);
  const webSocketEventData = useSelector(selectWebSocketEventData);
  const workgroupData = useSelector(selectWorkgroupData);
  const dispatch = useDispatch();
  const [isFirstLoad, setIsFirstLoad] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);
  const [errorObject, setErrorObject] = useState({});
  const [employeeTrades, setEmployeeTrades] = useState([]);
  const [filteredEmployeeTrades, setFilteredEmployeeTrades] = useState([]);
  const [searchedEmployeeTrades, setSearchedEmployeeTrades] = useState([]);
  const [isSearch, setIsSearch] = useState(false);

  // We need these Refs because changes to them are synchronous (as opposed to the state variables being async) and
  // knowing that, we can check the value of them upon getting a response back to ensure queries from changing the
  // table filters are not overwritten in the event a filter is changed in the middle of waiting for a response
  // See doesResponseQueryParamsMatchCurrentFilters and how it is used for more details.
  const dateTypeFilterRef = useRef("");
  const startDateFilterRef = useRef(null);
  const endDateFilterRef = useRef(null);
  const tempWebSocketDataRef = useRef([]);

  const isAdminPageRef = useRef(false);
  const tradeTableDesktopRef = useGridApiRef();
  const tradeTableMobileRef = useGridApiRef();
  const isApiSubscribedRef = useRef(true);
  const mobileOriginalTradeTypeFiltersRef = useRef(DEFAULT_EMPLOYEE_TRADES_TRADE_TYPE_FILTERS);
  const mobileOriginalStatusFiltersRef = useRef(DEFAULT_EMPLOYEE_TRADES_STATUS_FILTERS);
  const [isUserAuthorizedState, setIsUserAuthorizedState] = useState(false);
  const [isUserAuthorizedStateLoading, setIsUserAuthorizedStateLoading] = useState(true);

  useEffect(() => {
    if (!isUserAuthorizedState) {
      isUserAuthorized();
    } else {
      if (employeeTradeData.startDateFilter === null) {
        initializeFilterState();
      }
      if (employeeTradeData?.startDateFilterErrors === "" && employeeTradeData?.endDateFilterErrors === "") {
        updateEmployeeTradeEventList(
          userSession.eid,
          userSession.activeStation,
          employeeTradeData.dateTypeFilter,
          getStartDateFilterSafely(),
          getEndDateFilterSafely()
        );
      } else {
        setIsLoading(false);
      }
    }
    return () => {
      // Cleaning up lingering Promise handling...
      isApiSubscribedRef.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUserAuthorizedState]);

  useEffect(() => {
    tempWebSocketDataRef.current = webSocketEventData.tradeEmpEvents;
    if (Object.values(webSocketEventData.tradeEmpEvents).length > 0) {
      if (!isFirstLoad) {
        setEmployeeTrades((prevState) => {
          return mergeWebSocketEmpTrades(webSocketEventData.tradeEmpEvents, prevState);
        });
        tempWebSocketDataRef.current = {};
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [webSocketEventData.tradeEmpEvents]);

  useEffect(() => {
    let newFilteredTrades = filterTrades(
      employeeTrades,
      employeeTradeData.dateTypeFilter,
      getStartDateFilterSafely(),
      getEndDateFilterSafely(),
      employeeTradeData.tradeTypeFilters,
      employeeTradeData.statusFilters,
      employeeTradeData.workgroupFilters,
      isAdminPageRef.current
    );
    newFilteredTrades = sortTradesByFilter(employeeTradeData.mobileSortByValue, newFilteredTrades, userSession.eid);
    applyFilters(newFilteredTrades);
    if (employeeTradeData.searchString !== "") {
      handleSearchSubmit(employeeTradeData.searchString);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employeeTrades]);

  const isUserAuthorized = () => {
    isUserAuthorizedByServer(userSession).then((is200) => {
      if (is200) {
        setIsUserAuthorizedState(!isManager(userSession.jobCode));
      } else {
        logout();
      }
      setIsUserAuthorizedStateLoading(false);
    });
  };

  // We need this initialization since these fields are dependent on the userSession.activeStation value
  const initializeFilterState = () => {
    dispatch(
      updateStartDateFilter(serializeDateToRedux(DEFAULT_EMPLOYEE_TRADES_START_DATE_FILTER(userSession.activeStation)))
    );
    dispatch(
      updateEndDateFilter(serializeDateToRedux(DEFAULT_EMPLOYEE_TRADES_END_DATE_FILTER(userSession.activeStation)))
    );
  };

  const getStartDateFilterSafely = () => {
    return employeeTradeData.startDateFilter
      ? deserializeDateFromRedux(employeeTradeData.startDateFilter)
      : DEFAULT_EMPLOYEE_TRADES_START_DATE_FILTER(userSession.activeStation);
  };

  const getEndDateFilterSafely = () => {
    return employeeTradeData.endDateFilter
      ? deserializeDateFromRedux(employeeTradeData.endDateFilter)
      : DEFAULT_EMPLOYEE_TRADES_END_DATE_FILTER(userSession.activeStation);
  };

  const updateEmployeeTradeEventList = (eid, station, dateType, startDate, endDate) => {
    isApiSubscribedRef.current = true;
    setLoading(true);
    setIsError(false);
    setErrorObject({});
    dateTypeFilterRef.current = dateType;
    startDateFilterRef.current = startDate;
    endDateFilterRef.current = endDate;
    fetchLatestEmployeeTradeEvents(eid, dateType, startDate, endDate)
      .then((res) => res.json())
      .then((result) => {
        if (isApiSubscribedRef.current && doesResponseQueryParamsMatchCurrentFilters(result.queryParams)) {
          if (result.message) {
            if (result.message === ERROR_MAX_PAYLOAD_LIMIT_MSG) {
              setIsError(true);
              setErrorObject(tradeDataErrorCodeToErrorMessageMap.tradeDataMaxPayloadError);
            } else {
              throw new Error("Unexpected error");
            }
          } else {
            let scrubbedTradeFormEvents = getScrubbedTradeFormEvents(result.tradeFormEvents);
            setEmployeeTrades(mergeWebSocketEmpTrades(tempWebSocketDataRef.current, scrubbedTradeFormEvents));
            tempWebSocketDataRef.current = {};
            setIsLoading(false);
            setIsFirstLoad(false);
          }
        }
      })
      .catch((err) => {
        console.error("error happened!", err);
        setIsLoading(false);
        setIsError(true);
        setErrorObject(tradeDataErrorCodeToErrorMessageMap.unexpectedTradeDataError);
      });
  };

  const doesResponseQueryParamsMatchCurrentFilters = (responseQueryParams) => {
    return (
      responseQueryParams.eid === userSession.eid &&
      decodeURI(responseQueryParams.dateType) === dateTypeFilterRef.current &&
      responseQueryParams.startDate === startDateFilterRef.current.toMillis().toString() &&
      responseQueryParams.endDate === endDateFilterRef.current.toMillis().toString()
    );
  };

  const applySearch = (searchString, searchedTrades) => {
    if (searchString === "") {
      setIsSearch(false);
      setSearchedEmployeeTrades(searchedTrades);
      applyTradeTableScroll();
      setLoading(false);
    } else {
      setIsSearch(true);
      setSearchedEmployeeTrades(searchedTrades);
      applyTradeTableScroll();
      setLoading(false);
    }
  };

  const applyFilters = (filteredTrades) => {
    initializeSearchIndex(filteredTrades, isAdminPageRef.current);

    if (isSearch) {
      searchTrades(employeeTradeData.searchString, applySearch, isAdminPageRef.current);
    }

    setFilteredEmployeeTrades(filteredTrades);
  };

  const applyTradeTableScroll = () => {
    if (props.isDesktopView) {
      // This ref scrolls desktop trades to top
      const currentDesktop = tradeTableDesktopRef.current;
      if (currentDesktop !== null && currentDesktop.getRowModels && currentDesktop.getRowModels().size > 0) {
        currentDesktop.scrollToIndexes({ rowIndex: 0, colIndex: 0 });
      }
    } else {
      // This ref scrolls mobile trades to top
      const currentMobileScreen = tradeTableMobileRef.current;
      if (currentMobileScreen !== null && currentMobileScreen.children && currentMobileScreen.children.length > 0) {
        currentMobileScreen.children[0].parentNode.scrollTop = 0;
      }
    }
  };

  const setLoading = (isLoading) => {
    if (!isFirstLoad) {
      setIsLoading(isLoading);
    }
  };

  const handleDateTypeFilterChange = (event) => {
    updateEmployeeTradeEventList(
      userSession.eid,
      userSession.activeStation,
      event.target.value,
      deserializeDateFromRedux(employeeTradeData.startDateFilter),
      deserializeDateFromRedux(employeeTradeData.endDateFilter)
    );
    dispatch(updateDateTypeFilter(event.target.value));
    applyTradeTableScroll();
  };

  const handleStartDateFilterChange = (date) => {
    let newStartDateFilterErrors = validateStartDateFilter(
      date,
      deserializeDateFromRedux(employeeTradeData.endDateFilter),
      userSession.activeStation
    );
    dispatch(updateStartDateFilter(serializeDateToRedux(date)));
    dispatch(updateStartDateFilterErrors(newStartDateFilterErrors));
    if (newStartDateFilterErrors === "") {
      updateEmployeeTradeEventList(
        userSession.eid,
        userSession.activeStation,
        employeeTradeData.dateTypeFilter,
        date,
        deserializeDateFromRedux(employeeTradeData.endDateFilter)
      );
      dispatch(updateEndDateFilterErrors(""));
      applyTradeTableScroll();
    }
  };

  const handleEndDateFilterChange = (event) => {
    let date = set_time_to_end_of_day(event);
    let newEndDateFilterErrors = validateEndDateFilter(
      date,
      deserializeDateFromRedux(employeeTradeData.startDateFilter),
      userSession.activeStation
    );
    dispatch(updateEndDateFilter(serializeDateToRedux(date)));
    dispatch(updateEndDateFilterErrors(newEndDateFilterErrors));
    if (newEndDateFilterErrors === "") {
      updateEmployeeTradeEventList(
        userSession.eid,
        userSession.activeStation,
        employeeTradeData.dateTypeFilter,
        deserializeDateFromRedux(employeeTradeData.startDateFilter),
        date
      );
      dispatch(updateStartDateFilterErrors(""));
      applyTradeTableScroll();
    }
  };

  const handleTradeTypeFilterChange = (event) => {
    let newTradeTypeFilterSelectedList = event.target.value;
    if (newTradeTypeFilterSelectedList.includes("Select All")) {
      if (isTradeTypeSelectAllChecked()) {
        newTradeTypeFilterSelectedList = [];
      } else {
        newTradeTypeFilterSelectedList = ALL_TRADE_TYPES;
      }
    }
    setLoading(true);
    let newFilteredTrades = filterTrades(
      employeeTrades,
      employeeTradeData.dateTypeFilter,
      deserializeDateFromRedux(employeeTradeData.startDateFilter),
      deserializeDateFromRedux(employeeTradeData.endDateFilter),
      newTradeTypeFilterSelectedList,
      employeeTradeData.statusFilters,
      employeeTradeData.workgroupFilters,
      isAdminPageRef.current
    );
    dispatch(updateTradeTypeFilters(newTradeTypeFilterSelectedList));
    applyFilters(newFilteredTrades);
    setLoading(false);
    applyTradeTableScroll();
  };

  const isTradeTypeSelectAllChecked = () => {
    return employeeTradeData.tradeTypeFilters.length === ALL_TRADE_TYPES.length;
  };

  const isTradeTypeSelectAllIndeterminate = () => {
    return (
      employeeTradeData.tradeTypeFilters.length !== ALL_TRADE_TYPES.length &&
      employeeTradeData.tradeTypeFilters.length !== 0
    );
  };

  const handleStatusFilterChange = (event) => {
    let newStatusFilterSelectedList = event.target.value;
    if (newStatusFilterSelectedList.includes("Select All")) {
      if (isStatusSelectAllChecked()) {
        newStatusFilterSelectedList = [];
      } else {
        newStatusFilterSelectedList = ALL_STATUSES;
      }
    }

    setLoading(true);
    let newFilteredTrades = filterTrades(
      employeeTrades,
      employeeTradeData.dateTypeFilter,
      deserializeDateFromRedux(employeeTradeData.startDateFilter),
      deserializeDateFromRedux(employeeTradeData.endDateFilter),
      employeeTradeData.tradeTypeFilters,
      newStatusFilterSelectedList,
      employeeTradeData.workgroupFilters,
      isAdminPageRef.current
    );
    dispatch(updateStatusFilters(newStatusFilterSelectedList));
    applyFilters(newFilteredTrades);
    setLoading(false);
    applyTradeTableScroll();
  };

  const isStatusSelectAllChecked = () => {
    return employeeTradeData.statusFilters.length === ALL_STATUSES.length;
  };

  const isStatusSelectAllIndeterminate = () => {
    return (
      employeeTradeData.statusFilters.length !== ALL_STATUSES.length && employeeTradeData.statusFilters.length !== 0
    );
  };

  const handleWorkgroupFilterChange = (event) => {
    let newWorkgroupFilterSelectedList = event.target.value;
    if (newWorkgroupFilterSelectedList.includes("Select All")) {
      if (isWorkgroupSelectAllChecked()) {
        newWorkgroupFilterSelectedList = [];
      } else {
        newWorkgroupFilterSelectedList = getTradeWorkgroups(Object.keys(workgroupData));
      }
    }
    setLoading(true);
    let newFilteredTrades = filterTrades(
      employeeTrades,
      employeeTradeData.dateTypeFilter,
      deserializeDateFromRedux(employeeTradeData.startDateFilter),
      deserializeDateFromRedux(employeeTradeData.endDateFilter),
      employeeTradeData.tradeTypeFilters,
      employeeTradeData.statusFilters,
      newWorkgroupFilterSelectedList,
      isAdminPageRef.current
    );
    dispatch(updateWorkgroupFilters(newWorkgroupFilterSelectedList));
    applyFilters(newFilteredTrades);
    setLoading(false);
    applyTradeTableScroll();
  };

  const isWorkgroupSelectAllIndeterminate = () => {
    return (
      employeeTradeData.workgroupFilters.length !== getTradeWorkgroups(Object.keys(workgroupData)).length &&
      employeeTradeData.workgroupFilters.length !== 0
    );
  };

  const isWorkgroupSelectAllChecked = () => {
    return employeeTradeData.workgroupFilters.length === getTradeWorkgroups(Object.keys(workgroupData)).length;
  };

  const handleSearchChange = (event) => {
    dispatch(updateSearchString(event.target.value));
  };

  const handleSearchBlur = (event) => {
    if (event.target.value.trim() === "") {
      handleSearchSubmit("");
    }
  };

  const handleSearchEnterKeyPressed = (event) => {
    if (event.key === "Enter") {
      handleSearchSubmit(employeeTradeData.searchString);
    }
  };

  const handleSearchCancel = () => {
    dispatch(updateSearchString(""));
    handleSearchSubmit("");
  };

  const handleSearchSubmit = (newSearchString) => {
    dispatch(updateSearchStringErrors(""));
    const newSearchStringTrimmed = newSearchString.trim();
    if (
      !isNaN(parseInt(newSearchStringTrimmed)) &&
      parseInt(newSearchStringTrimmed) >= 10000000 &&
      parseInt(newSearchStringTrimmed) <= 99999999
    ) {
      dispatch(updateTradeFoundByReferenceNumberSearchIsLoading(true));
      fetchTradeDataByReferenceNumber(
        newSearchStringTrimmed,
        userSession.eid,
        employeeTradeData.stationFilter,
        isAdminPageRef.current
      )
        .then((res) => res.json())
        .then((result) => {
          if (result.message) {
            dispatch(updateSearchStringErrors(result.message));
            dispatch(updateTradeFoundByReferenceNumberSearchIsLoading(false));
          } else {
            let tradeFormEvent = result.latestTradeFormEvent;
            let scrubbedTradeFormEvent = getScrubbedTradeFormEvent(tradeFormEvent);
            dispatch(updateTradeFoundByReferenceNumberSearch(tradeFormEvent));
            dispatch(updateTradeFoundByReferenceNumberSearchIsLoading(false));
            applySearch(newSearchStringTrimmed, [scrubbedTradeFormEvent]);
            dispatch(updateTradeFoundByReferenceNumberSearchIsDialogOpen(true));
          }
        })
        .catch((err) => {
          console.error("error happened!", err);
          // TODO: This should display an error instead of closing the dialog for a better UX
          dispatch(updateTradeFoundByReferenceNumberSearchIsDialogOpen(false));
        });
    } else {
      searchTrades(newSearchStringTrimmed, applySearch, isAdminPageRef.current);
    }
  };

  const handleClickTradeFoundByReferenceNumberSearchDialogOpen = () => {
    dispatch(updateTradeFoundByReferenceNumberSearchIsDialogOpen(true));
  };

  const handleClickTradeFoundByReferenceNumberSearchDialogClose = () => {
    dispatch(updateTradeFoundByReferenceNumberSearchIsDialogOpen(false));
    dispatch(updateTradeFoundByReferenceNumberSearch(null));
  };

  // START - Mobile only functions

  const sortMobileTrades = (sortValue, trades) => {
    let sortedTrades = [...trades];
    sortTradesByFilter(sortValue, sortedTrades, userSession.eid);
    applyFilters(sortedTrades);
    setFilteredEmployeeTrades(sortedTrades);
  };

  const revertMobileFilters = () => {
    setLoading(true);
    let newFilteredTrades = filterTrades(
      employeeTrades,
      employeeTradeData.dateTypeFilter,
      deserializeDateFromRedux(employeeTradeData.startDateFilter),
      deserializeDateFromRedux(employeeTradeData.endDateFilter),
      mobileOriginalTradeTypeFiltersRef.current,
      mobileOriginalStatusFiltersRef.current,
      employeeTradeData.workgroupFilters,
      isAdminPageRef.current
    );
    dispatch(updateTradeTypeFilters(mobileOriginalTradeTypeFiltersRef.current));
    dispatch(updateStatusFilters(mobileOriginalStatusFiltersRef.current));
    applyFilters(newFilteredTrades);
    setLoading(false);
    applyTradeTableScroll();
  };

  const handleMobileSortByChange = (event) => {
    dispatch(updateMobileSortByValue(event.target.value));
    sortMobileTrades(event.target.value, filteredEmployeeTrades);
    applyTradeTableScroll();
  };

  const handleClickMobileFilterDialogOpen = () => {
    mobileOriginalTradeTypeFiltersRef.current = employeeTradeData.tradeTypeFilters;
    mobileOriginalStatusFiltersRef.current = employeeTradeData.statusFilters;
    dispatch(updateIsMobileFilterDialogOpen(true));
  };

  const handleClickMobileFilterDialogClose = () => {
    revertMobileFilters();
    dispatch(updateIsMobileFilterDialogOpen(false));
  };

  const handleClickMobileFilterDialogApplyFiltersButton = () => {
    if (employeeTradeData.tradeTypeFilters.length > 0 && employeeTradeData.statusFilters.length > 0) {
      if (
        mobileOriginalStatusFiltersRef.current === employeeTradeData.statusFilters &&
        mobileOriginalTradeTypeFiltersRef.current === employeeTradeData.tradeTypeFilters
      ) {
        dispatch(updateIsMobileFilterDialogOpen(false));
        return;
      }

      sortMobileTrades(employeeTradeData.mobileSortByValue, filteredEmployeeTrades);
      applyTradeTableScroll();
      dispatch(updateIsMobileFilterDialogOpen(false));
    }
  };

  const renderRequestTradeButton = () => {
    const tradeFormChildItems = props.featureConfig.childItems;
    const requestTrade = tradeFormChildItems.find((item) => item.key === FEATURES.REQUEST_TRADE);
    if (!!requestTrade) {
      return (
        <Button
          className={props.isDesktopView ? classes.requestTradeButtonDesktop : classes.requestTradeButtonMobile}
          href={`#${requestTrade.path}`}
          color="primary"
          variant="contained"
        >
          <Typography variant="body2" className={classes.buttonText}>
            {REQUEST_TRADE_TITLE.toUpperCase()}
          </Typography>
        </Button>
      );
    } else {
      return "";
    }
  };

  // END - Mobile only functions

  if (!isUserAuthorizedState && !isUserAuthorizedStateLoading) {
    return <UnauthorizedMessage />;
  }
  const breadcrumbsList = [BREADCRUMBS_TITLE.HOME, BREADCRUMBS_TITLE.MY_TRADES];
  const breadcrumbsLink = [HOME_PATH];
  return (
    <div className="App-contents" data-testid="employee-trades-page">
      {isUserAuthorizedStateLoading ? (
        <PageLoading />
      ) : (
        <>
          {props.isDesktopView && (
            <StarsBreadcrumbs
              breadcrumbsLinks={breadcrumbsLink}
              breadcrumbsList={breadcrumbsList}
              isConfirmationRequired={false}
            />
          )}
          <TradeFilterForm
            loggedInUserData={userSession}
            isAdminPage={isAdminPageRef.current}
            title={employeeTradesPageTitle}
            trades={employeeTrades}
            dateTypeFilter={employeeTradeData.dateTypeFilter}
            onDateTypeFilterChange={handleDateTypeFilterChange}
            startDateFilter={getStartDateFilterSafely()}
            startDateFilterErrors={employeeTradeData.startDateFilterErrors}
            onStartDateFilterChange={handleStartDateFilterChange}
            endDateFilter={getEndDateFilterSafely()}
            endDateFilterErrors={employeeTradeData.endDateFilterErrors}
            onEndDateFilterChange={handleEndDateFilterChange}
            tradeTypeFilters={employeeTradeData.tradeTypeFilters}
            allTradeTypeFilters={ALL_TRADE_TYPES}
            onTradeTypeFilterChange={handleTradeTypeFilterChange}
            isTradeTypeSelectAllChecked={isTradeTypeSelectAllChecked}
            isTradeTypeSelectAllIndeterminate={isTradeTypeSelectAllIndeterminate}
            statusFilters={employeeTradeData.statusFilters}
            allStatusFilters={ALL_STATUSES}
            onStatusFilterChange={handleStatusFilterChange}
            isStatusSelectAllChecked={isStatusSelectAllChecked}
            isStatusSelectAllIndeterminate={isStatusSelectAllIndeterminate}
            workgroupFilters={employeeTradeData.workgroupFilters}
            allWorkgroupFilters={getTradeWorkgroups(Object.keys(workgroupData))}
            onWorkgroupFilterChange={handleWorkgroupFilterChange}
            isWorkgroupSelectAllChecked={isWorkgroupSelectAllChecked}
            isWorkgroupSelectAllIndeterminate={isWorkgroupSelectAllIndeterminate}
            stationFilter={userSession.activeStation}
            searchString={employeeTradeData.searchString}
            searchStringErrors={employeeTradeData.searchStringErrors}
            tradeFoundByReferenceNumberSearch={
              employeeTradeData.tradeFoundByReferenceNumberSearch
                ? getScrubbedTradeFormEvent(employeeTradeData.tradeFoundByReferenceNumberSearch)
                : employeeTradeData.tradeFoundByReferenceNumberSearch
            }
            tradeFoundByReferenceNumberSearchIsLoading={employeeTradeData.tradeFoundByReferenceNumberSearchIsLoading}
            tradeFoundByReferenceNumberSearchIsDialogOpen={
              employeeTradeData.tradeFoundByReferenceNumberSearchIsDialogOpen
            }
            onClickTradeFoundByReferenceNumberSearchDialogOpen={handleClickTradeFoundByReferenceNumberSearchDialogOpen}
            onClickTradeFoundByReferenceNumberSearchDialogClose={
              handleClickTradeFoundByReferenceNumberSearchDialogClose
            }
            onSearchChange={handleSearchChange}
            onSearchBlur={handleSearchBlur}
            onSearchEnterKeyPressed={handleSearchEnterKeyPressed}
            onSearchCancel={handleSearchCancel}
            onSearchSubmit={handleSearchSubmit}
            mobileSortByValue={employeeTradeData.mobileSortByValue}
            onMobileSortByChange={handleMobileSortByChange}
            onMobileFilterDialogClose={handleClickMobileFilterDialogClose}
            isMobileFilterDialogOpen={employeeTradeData.isMobileFilterDialogOpen}
            onClickMobileFilterDialogOpen={handleClickMobileFilterDialogOpen}
            onClickMobileFilterDialogApplyFiltersButton={handleClickMobileFilterDialogApplyFiltersButton}
            isDesktopView={props.isDesktopView}
          />

          {renderRequestTradeButton()}

          <EmployeeTradeTable
            loggedInUserData={userSession}
            employeeTrades={isSearch ? searchedEmployeeTrades : filteredEmployeeTrades}
            isAdminPage={false}
            isFirstLoad={isFirstLoad}
            isLoading={isLoading}
            isError={isError}
            errorObject={errorObject}
            tradeTableDesktopRef={tradeTableDesktopRef}
            tradeTableMobileRef={tradeTableMobileRef}
            isDesktopView={props.isDesktopView}
          />
        </>
      )}
    </div>
  );
};

export default MyTradesPage;
